/**
 * Session trim + wrapped clips — operator card on /play/<sid>
 * (docs/SESSION_TRIM_AND_CLIPS.md).
 *
 * Trim = range DELETE at 6 s segment granularity, never a media
 * operation: pick the kept segment range (numeric inputs, or "mark
 * from playhead" against the synced viewer's current position) and the
 * backend deletes everything outside it for every camera. Irreversible
 * — the confirm dialog shows the dry-run's exact segment/MB counts
 * before anything is deleted.
 *
 * Clip = the existing zero-re-encode assembler over the same range:
 * one progressive .mp4 per camera, downloaded through the authed
 * blob path. The recipient re-encodes or cuts further on their side.
 *
 * Segment mapping: the coverage endpoint's per-segment UTC ranges
 * (segments.json sidecar). Legacy sessions without a sidecar fall back
 * to [1, segment_count] with no wall-clock labels.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Download, Scissors, SkipBack, SkipForward } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import api from '../services/api';
import { FleetSessionMeta, SessionCoverage, SessionTrimResult } from '../types';

interface SegSpan {
  seg: number;
  startMs: number;
  endMs: number;
}

interface PendingTrim {
  keepStart: number;
  keepEnd: number; // -1 = open upper bound
  label: string;
}

interface Props {
  sessionId: string;
  meta: FleetSessionMeta | null;
  /** Synced viewer playhead (UTC ms); null until the player reports. */
  playheadUtcMs: number | null;
  /** Camera ids from the buffered session (fallback when no sidecar). */
  cameras: string[];
  /** Called after a successful (non-dry-run) trim so the page refreshes. */
  onTrimmed?: () => void;
}

const fmtMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const fmtClock = (ms: number) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const SessionTrimClipCard: React.FC<Props> = ({
  sessionId,
  meta,
  playheadUtcMs,
  cameras,
  onTrimmed,
}) => {
  const [coverage, setCoverage] = useState<SessionCoverage | null>(null);
  const [selStart, setSelStart] = useState<number | null>(null);
  const [selEnd, setSelEnd] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [pendingTrim, setPendingTrim] = useState<PendingTrim | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .getSessionCoverage(sessionId)
      .then((c) => {
        if (!cancelled) setCoverage(c);
      })
      .catch(() => {
        if (!cancelled) setCoverage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshKey]);

  // Merge the per-camera sidecar entries into one span per seg number
  // (min start / max end across cameras), sorted — the playhead↔segment
  // mapping and the time labels both read from this.
  const spans: SegSpan[] = useMemo(() => {
    const bySeg = new Map<number, SegSpan>();
    for (const entries of Object.values(coverage?.cameras ?? {})) {
      for (const e of entries) {
        const cur = bySeg.get(e.seg_num);
        if (!cur) {
          bySeg.set(e.seg_num, { seg: e.seg_num, startMs: e.start_utc_ms, endMs: e.end_utc_ms });
        } else {
          cur.startMs = Math.min(cur.startMs, e.start_utc_ms);
          cur.endMs = Math.max(cur.endMs, e.end_utc_ms);
        }
      }
    }
    return [...bySeg.values()].sort((a, b) => a.seg - b.seg);
  }, [coverage]);

  // Sidecar-less legacy sessions: fall back to [1, segment_count].
  const segMin = spans.length ? spans[0].seg : meta?.segment_count ? 1 : null;
  const segMax = spans.length
    ? spans[spans.length - 1].seg
    : meta?.segment_count || null;

  useEffect(() => {
    if (segMin != null && segMax != null && (selStart == null || selEnd == null)) {
      setSelStart(segMin);
      setSelEnd(segMax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segMin, segMax]);

  const sessionStartMs = spans.length ? spans[0].startMs : null;

  const utcToSeg = (utcMs: number): number | null => {
    if (!spans.length) return null;
    for (const s of spans) {
      if (utcMs < s.startMs) return s.seg; // gap/before → snap forward
      if (utcMs <= s.endMs) return s.seg;
    }
    return spans[spans.length - 1].seg;
  };

  const segLabel = (seg: number | null): string => {
    if (seg == null) return '—';
    if (sessionStartMs == null) return `#${seg}`;
    const span = spans.find((s) => s.seg === seg);
    return span ? `#${seg} · ${fmtClock(span.startMs - sessionStartMs)}` : `#${seg}`;
  };

  const playheadSeg = playheadUtcMs != null ? utcToSeg(playheadUtcMs) : null;
  const cameraIds = useMemo(() => {
    const ids = new Set<string>(Object.keys(coverage?.cameras ?? {}));
    cameras.forEach((c) => ids.add(c));
    return [...ids].sort();
  }, [coverage, cameras]);

  const isRecording = meta?.state === 'recording';
  const rangeValid =
    selStart != null && selEnd != null && selStart <= selEnd && segMin != null;

  const clampSeg = (v: number) =>
    Math.max(segMin ?? 0, Math.min(segMax ?? v, v));

  const handleDownload = async (cameraId: string) => {
    if (!rangeValid) return;
    setDownloading(cameraId);
    setDownloadError(null);
    try {
      // Full-range selection → plain whole-session download (no params).
      const whole = selStart === segMin && selEnd === segMax;
      await api.downloadSessionClip(
        sessionId,
        cameraId,
        whole ? undefined : selStart!,
        whole ? undefined : selEnd!,
      );
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setDownloadError(
        status === 404
          ? `no uploaded segments in #${selStart}–#${selEnd} for ${cameraId} — use "Request archive" on the device first, then retry`
          : status === 413
            ? 'selection is over the single-file download cap — pick a narrower range'
            : `clip download failed for ${cameraId}`,
      );
    } finally {
      setDownloading(null);
    }
  };

  if (segMin == null || segMax == null) {
    return null; // nothing uploaded yet — no ranges to trim or clip
  }

  return (
    <div className="mt-3 rounded border border-border bg-bg-surface p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Scissors className="size-4 text-accent" />
        <span className="font-mono text-accent">Trim &amp; Clips</span>
        <Badge variant="outline" className="font-mono text-fg-secondary">
          segments #{segMin}–#{segMax}
        </Badge>
        {!spans.length && (
          <Badge variant="outline" className="border-status-warning font-mono text-status-warning">
            no timing sidecar — segment numbers only
          </Badge>
        )}
        <div className="flex-1" />
        <span className="font-mono text-[11px] text-fg-muted">
          6 s segment granularity · no re-encode
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="font-mono text-[11px] text-fg-muted">from segment</label>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="h-8 w-24 font-mono text-xs"
              min={segMin}
              max={segMax}
              value={selStart ?? ''}
              onChange={(e) => setSelStart(clampSeg(Number(e.target.value)))}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8 font-mono text-[11px]"
              disabled={playheadSeg == null}
              onClick={() => playheadSeg != null && setSelStart(clampSeg(playheadSeg))}
              title="Set the range start to the player's current position"
            >
              <SkipBack className="size-3" /> playhead
            </Button>
          </div>
          <span className="font-mono text-[11px] text-fg-muted">{segLabel(selStart)}</span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-mono text-[11px] text-fg-muted">to segment</label>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="h-8 w-24 font-mono text-xs"
              min={segMin}
              max={segMax}
              value={selEnd ?? ''}
              onChange={(e) => setSelEnd(clampSeg(Number(e.target.value)))}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8 font-mono text-[11px]"
              disabled={playheadSeg == null}
              onClick={() => playheadSeg != null && setSelEnd(clampSeg(playheadSeg))}
              title="Set the range end to the player's current position"
            >
              <SkipForward className="size-3" /> playhead
            </Button>
          </div>
          <span className="font-mono text-[11px] text-fg-muted">{segLabel(selEnd)}</span>
        </div>

        <div className="flex-1" />

        <div className="flex flex-col gap-1">
          <label className="font-mono text-[11px] text-fg-muted">
            clip download (wrapped, per camera)
          </label>
          <div className="flex flex-wrap gap-1">
            {cameraIds.map((cam) => (
              <Button
                key={cam}
                size="sm"
                variant="outline"
                className="h-8 border-accent font-mono text-[11px] text-accent hover:bg-accent/10"
                disabled={!rangeValid || downloading != null}
                onClick={() => void handleDownload(cam)}
              >
                {downloading === cam ? <Spinner size={12} /> : <Download className="size-3" />}
                {cam}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {downloadError && (
        <p className="mt-2 font-mono text-[11px] text-status-warning">{downloadError}</p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <span className="font-mono text-[11px] text-fg-muted">trim (deletes from cloud):</span>
        <Badge variant="outline" className="font-mono text-[11px] text-fg-secondary">
          playhead = {segLabel(playheadSeg)}
        </Badge>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-status-error font-mono text-[11px] text-status-error hover:bg-status-error/10"
          disabled={isRecording || !rangeValid || (selStart === segMin && selEnd === segMax)}
          onClick={() =>
            setPendingTrim({
              keepStart: selStart!,
              keepEnd: selEnd === segMax ? -1 : selEnd!,
              label: `keep only #${selStart}–#${selEnd}`,
            })
          }
        >
          <Scissors className="size-3" /> Trim to selection
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-status-error font-mono text-[11px] text-status-error hover:bg-status-error/10"
          disabled={isRecording || playheadSeg == null || playheadSeg <= segMin}
          title={
            playheadSeg == null
              ? 'No playhead yet — press play or seek the video first'
              : playheadSeg <= segMin
                ? 'Playhead is at the first segment — nothing before it'
                : `Delete segments #${segMin}–#${playheadSeg - 1}, keep the rest`
          }
          onClick={() =>
            playheadSeg != null &&
            setPendingTrim({
              keepStart: playheadSeg,
              keepEnd: -1,
              label: `drop everything before #${playheadSeg}`,
            })
          }
        >
          Drop before playhead
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-status-error font-mono text-[11px] text-status-error hover:bg-status-error/10"
          disabled={isRecording || playheadSeg == null || playheadSeg >= segMax}
          title={
            playheadSeg == null
              ? 'No playhead yet — press play or seek the video first'
              : playheadSeg >= segMax
                ? 'Playhead is at the last segment — nothing after it'
                : `Delete segments #${playheadSeg + 1}–#${segMax}, keep the rest`
          }
          onClick={() =>
            playheadSeg != null &&
            setPendingTrim({
              keepStart: segMin,
              keepEnd: playheadSeg,
              label: `drop everything after #${playheadSeg}`,
            })
          }
        >
          Drop after playhead
        </Button>
        {isRecording && (
          <span className="font-mono text-[11px] text-fg-muted">
            trimming is disabled while recording
          </span>
        )}
      </div>

      <TrimConfirmDialog
        sessionId={sessionId}
        pending={pendingTrim}
        onClose={() => setPendingTrim(null)}
        onDone={() => {
          setPendingTrim(null);
          setSelStart(null);
          setSelEnd(null);
          setRefreshKey((k) => k + 1);
          onTrimmed?.();
        }}
      />
    </div>
  );
};

interface TrimDialogProps {
  sessionId: string;
  pending: PendingTrim | null;
  onClose: () => void;
  onDone: () => void;
}

/** Destructive confirm — dry-runs the trim on open so the operator sees
 *  the exact segment/MB counts before anything is deleted. */
const TrimConfirmDialog: React.FC<TrimDialogProps> = ({
  sessionId,
  pending,
  onClose,
  onDone,
}) => {
  const [preview, setPreview] = useState<SessionTrimResult | null>(null);
  const [evictPhone, setEvictPhone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPreview(null);
    setError(null);
    setEvictPhone(false);
    if (!pending) return;
    let cancelled = false;
    api
      .trimSession(sessionId, pending.keepStart, pending.keepEnd, { dryRun: true })
      .then((r) => {
        if (!cancelled) setPreview(r);
      })
      .catch((e) => {
        if (!cancelled) {
          const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail;
          setError(detail ?? 'failed to preview the trim');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, pending]);

  const handleConfirm = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await api.trimSession(sessionId, pending.keepStart, pending.keepEnd, {
        evictPhoneCopies: evictPhone,
      });
      onDone();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? 'trim failed');
    } finally {
      setBusy(false);
    }
  };

  const approxSeconds = preview ? preview.segments_deleted * 6 : 0;

  return (
    <Dialog open={!!pending} onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="border-status-error/50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-mono">
            <Scissors className="size-4 text-status-error" />
            Trim session — {pending?.label}
          </DialogTitle>
        </DialogHeader>

        {!preview && !error && (
          <div className="flex items-center gap-2 py-2 font-mono text-xs text-fg-secondary">
            <Spinner size={14} /> computing what would be deleted…
          </div>
        )}

        {preview && (
          <div className="space-y-2 py-1 font-mono text-xs text-fg-secondary">
            <p>
              <span className="text-status-error">
                {preview.segments_deleted} segment(s) · ~{fmtClock(approxSeconds * 1000)} min ·{' '}
                {fmtMb(preview.bytes_freed)}
              </span>{' '}
              will be permanently deleted from cloud storage.
            </p>
            <ul className="ml-4 list-disc">
              {Object.entries(preview.cameras).map(([cam, st]) => (
                <li key={cam}>
                  {cam}: −{st.segments_deleted} seg ({fmtMb(st.bytes_freed)}), keeps{' '}
                  {st.segments_kept}
                </li>
              ))}
            </ul>
            <p>
              Kept range: #{preview.keep_seg_start}–
              {preview.keep_seg_end === -1 ? 'end' : `#${preview.keep_seg_end}`} (
              {preview.segments_kept} segment(s)). This cannot be undone.
            </p>
            <label className="flex cursor-pointer items-center gap-2 pt-1 text-fg">
              <Checkbox
                checked={evictPhone}
                onCheckedChange={(v) => setEvictPhone(v === true)}
              />
              also release the phone&apos;s local copies for eviction
            </label>
          </div>
        )}

        {error && <p className="font-mono text-xs text-status-warning">{error}</p>}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-status-error font-mono text-white hover:bg-status-error/90"
            disabled={!preview || busy || preview.segments_deleted === 0}
            onClick={() => void handleConfirm()}
          >
            {busy ? <Spinner size={14} /> : <Scissors className="size-3" />}
            Delete {preview?.segments_deleted ?? 0} segment(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionTrimClipCard;
